import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Trash2, Loader2, Wand2, Globe, Lock, Download, Share2, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { magicGallery, magicDelete, magicToggleShare, magicFileUrl } from "@/lib/magicApi";

export default function MagicGallery() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/magic/gallery"); return; }
    magicGallery(120).then((r) => setItems(r.generations || [])).catch(() => toast.error("Couldn't load")).finally(() => setBusy(false));
  }, [user, loading, nav]);

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.style_name}" transformation?`)) return;
    try { await magicDelete(g.id); setItems((l) => l.filter((x) => x.id !== g.id)); setPreview(null); toast.success("Deleted"); }
    catch { toast.error("Couldn't delete"); }
  };
  const togglePublic = async (g) => {
    const next = !g.is_public;
    setItems((l) => l.map((x) => x.id === g.id ? { ...x, is_public: next } : x));
    try { await magicToggleShare(g.id, next); toast.success(next ? "Shareable" : "Private"); }
    catch { setItems((l) => l.map((x) => x.id === g.id ? { ...x, is_public: !next } : x)); toast.error("Couldn't update"); }
  };
  const download = async (g) => {
    try {
      const r = await fetch(magicFileUrl(g.result_path), { credentials: "include" });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `jixxeo-magic-${g.style_slug}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
  };

  if (busy) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="magic-gallery">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#FF00E5]/15 text-[#FF00E5] flex items-center justify-center">
          <Sparkles className="w-4 h-4" fill="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Your creations</div>
          <div className="font-black text-2xl">My gallery</div>
        </div>
        <Link to="/magic" data-testid="gallery-new-btn"
          className="inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
          <Wand2 className="w-3 h-3" /> New magic
        </Link>
      </div>

      {items.length === 0 ? (
        <div data-testid="gallery-empty" className="rounded-3xl border border-[#FF00E5]/30 bg-gradient-to-br from-[#FF00E5]/15 via-[#150525] to-[#0A0512] p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#FF00E5] text-white flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7" fill="currentColor" />
          </div>
          <div className="font-black text-xl">Your gallery is empty</div>
          <div className="text-sm text-white/60 mt-1">Pick a style and let Jixxeo Magic transform you.</div>
          <Link to="/magic" data-testid="gallery-empty-cta"
            className="mt-5 inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full">
            Browse 49 styles <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5" data-testid="gallery-grid">
          {items.map((g) => (
            <button key={g.id} data-testid={`gallery-item-${g.id}`} onClick={() => setPreview(g)}
              className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 hover:border-[#FF00E5]/60 transition-colors bg-black group">
              <img src={magicFileUrl(g.result_path)} alt={g.style_name}
                className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2">
                <div className="text-white font-black text-xs leading-tight text-left truncate">{g.style_name}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/60 flex items-center gap-1">
                  {g.is_public ? <Globe className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
                  {new Date(g.created_at).toLocaleDateString()}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {preview && <PreviewModal gen={preview} onClose={() => setPreview(null)}
        onDelete={() => del(preview)} onTogglePublic={() => togglePublic(preview)} onDownload={() => download(preview)} />}
    </div>
  );
}

function PreviewModal({ gen, onClose, onDelete, onTogglePublic, onDownload }) {
  return (
    <div data-testid="gallery-preview" onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/50">{gen.category}</div>
            <div className="font-black text-xl truncate">{gen.style_name}</div>
          </div>
          <button data-testid="preview-close" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center font-black">✕</button>
        </div>
        <img src={magicFileUrl(gen.result_path)} alt={gen.style_name}
          className="w-full max-h-[70vh] object-contain rounded-2xl border border-white/10 bg-black" />
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid="preview-download" onClick={onDownload}
            className="inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button data-testid="preview-share" onClick={async () => {
            const url = `${window.location.origin}/magic/gallery`;
            try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
            catch { toast.error("Couldn't copy"); }
          }}
            className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <button data-testid="preview-public" onClick={onTogglePublic}
            className={"inline-flex items-center gap-1 font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full border " +
              (gen.is_public ? "border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]" : "border-white/20 bg-white/5 text-white/70")}>
            {gen.is_public ? <><Globe className="w-3.5 h-3.5" /> Public</> : <><Lock className="w-3.5 h-3.5" /> Private</>}
          </button>
          <button data-testid="preview-delete" onClick={onDelete}
            className="ml-auto inline-flex items-center gap-1 border border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171] font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
